<template>
  <v-main>
    <v-container fluid>
      <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
      <v-card>
        <div class="d-flex mx-7 pt-5 justify-space-between">
          <h5 class="text-h5">Data Artikel</h5>
          <v-btn v-if="!isEdit" density="compact" @click="isEdit = true">
            Edit <v-icon class="ml-1">mdi-pen</v-icon>
          </v-btn>
        </div>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      v-model="currentData.judul"
                      :disabled="!isEdit"
                      label="Judul"
                      clearable
                      dense
                      outlined
                      required
                    ></v-text-field>
                    <v-textarea
                      v-model="currentData.konten"
                      :disabled="!isEdit"
                      label="Konten"
                      clearable
                      dense
                      outlined
                      required
                    ></v-textarea>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="currentData.judul_en"
                      :disabled="!isEdit"
                      label="Judul Inggris"
                      clearable
                      dense
                      outlined
                      required
                    ></v-text-field>
                    <v-textarea
                      v-model="currentData.konten_en"
                      :disabled="!isEdit"
                      label="Konten Inggris"
                      clearable
                      dense
                      outlined
                      required
                    ></v-textarea>
                  </v-col>
                </v-row>
                <v-combobox
                  v-model="currentData.selectedKategori"
                  :disabled="!isEdit"
                  label="Kategori"
                  dense
                  outlined
                  :items="currentData.items_kategori"
                  item-text="nama"
                ></v-combobox>
                <v-combobox
                  v-model="currentData.selectedHide"
                  :disabled="!isEdit"
                  label="Publish"
                  dense
                  outlined
                  :items="currentData.items_hide"
                  item-text="name"
                ></v-combobox>
                <v-file-input
                  v-model="filefoto"
                  :disabled="!isEdit"
                  label="Upload Foto Thumbnail (Cover)"
                  accept="image/*"
                  prepend-icon="mdi-image"
                  clearable
                  dense
                  outlined
                  @change="loadImage"
                >
                </v-file-input>
                <v-row v-if="currentData.FotoUrl" class="my-5">
                  <v-col cols="12" sm="6" md="4">
                    <v-responsive :aspect-ratio="16 / 9">
                      <v-img :src="currentData.FotoUrl" contain></v-img>
                    </v-responsive>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <div v-if="isEdit">
              <CardGallery />
            </div>
          </v-container>
        </v-card-text>
        <div style="height: 60px"></div>
        <v-card-actions v-if="isEdit">
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="isEdit = false">
            Cancel
          </v-btn>
          <v-btn color="blue darken-1" text @click="doSave"> Save </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </v-main>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "../../utils/utils.js";
import KategoriArtikelService from "../../services/web_content/KategoriArtikelService";
import CommonService from "../../services/CommonService";
import ArtikelService from "../../services/web_content/ArtikelService";
import CardGallery from "../../components/CardGallery.vue";

// Router instance
const router = useRouter();

// Static data
const breadcrumbs = [
  {
    text: "Artikel",
    disabled: false,
    href: "/webcontent/artikel",
  },
  {
    text: "Edit Artikel",
    disabled: true,
    href: "breadcrumbs_dashboard",
  },
];

// Reactive state
const isEdit = ref(false);
const filefoto = ref(null);
const currentData = ref({
  id: "",
  judul: "",
  konten: null,
  judul_en: "",
  konten_en: null,
  selectedKategori: "",
  selectedHide: "",
  foto_url: "",
  FotoUrl: "",
  items_hide: [
    { code: 1, name: "Publish" },
    { code: 2, name: "Hide" },
  ],
  items_kategori: [],
});

// Functions (previously methods)
const loadImage = (file) => {
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      currentData.value.FotoUrl = e.target.result;
    };
    reader.readAsDataURL(file);
  } else {
    currentData.value.FotoUrl = "";
    currentData.value.foto_url = null;
  }
};

const getData = () => {
  const data = sessionStorage.getItem("item_artikel");
  if (!data) return;

  const artikel = JSON.parse(data);
  currentData.value.id = artikel.id;
  currentData.value.judul = artikel.title;
  currentData.value.konten = artikel.article;
  currentData.value.judul_en = artikel.title_en;
  currentData.value.konten_en = artikel.article_en;
  currentData.value.FotoUrl = artikel.photo;

  currentData.value.selectedHide = currentData.value.items_hide.find(
    (entry) => entry.code == artikel.data_status,
  );

  if (artikel.article_type) {
    currentData.value.selectedKategori = currentData.value.items_kategori.find(
      (entry) => entry.id == artikel.article_type,
    );
  } else {
    currentData.value.selectedKategori = null;
  }
};

const getListKategori = async () => {
  try {
    const res = await KategoriArtikelService.kategoriArtikelListAll();
    currentData.value.items_kategori = res.data.list_data;
    getData(); // Call getData after categories are loaded
  } catch (err) {
    console.error(err);
  }
};

const doSave = async () => {
  if (!currentData.value.judul || !currentData.value.konten) {
    CommonService.showWarningToast("Lengkapi data terlebih dahulu!");
    return;
  }

  try {
    await ArtikelService.update(currentData.value, filefoto.value);
    CommonService.showSuccessToast("Update artikel berhasil");
    router.push("/webcontent/artikel");
  } catch (err) {
    console.error(err);
    CommonService.showErrorToast("Update artikel gagal. Coba lagi");
  }
};

// Lifecycle hook
onMounted(() => {
  getListKategori();
});
</script>
